"use client";

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { usePathname, useRouter } from "next/navigation";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { Button } from "@/design-system";
import { Icon } from "@/design-system/icons";
import { useAuth } from "@/contexts/AuthContext";
import {
  subscribeToActivityEvents,
  trackActivity,
  type ClientActivityEvent,
} from "@/lib/activityTracking";
import { resolveAgentDisplayName } from "@/lib/agentBranding";
import { apiRequest } from "@/utils/api";
import type {
  OnboardingProgressAction,
  OnboardingStateResponse,
  OnboardingStepId,
} from "@/types/onboarding";
import styles from "./GuidedOnboardingPanel.module.css";

const AGENT_DISPLAY_NAME = resolveAgentDisplayName();
const ONBOARDING_QUERY_KEY = ["users", "me", "onboarding"] as const;
const ROUTE_COMPLETED_STEPS = new Set<OnboardingStepId>([
  "dashboard_overview",
  "surveys_overview",
  "responses_review",
  "organization_context",
]);

function isRouteMatch(pathname: string, route: string) {
  return pathname === route || pathname.startsWith(`${route}/`);
}

function isSurveyDetailRoute(pathname: string) {
  return /^\/surveys\/[^/]+/.test(pathname);
}

function getActivityAction(event: ClientActivityEvent) {
  return typeof event.payload.action === "string" ? event.payload.action : "";
}

function shouldCompleteStepFromActivity(
  stepId: OnboardingStepId,
  event: ClientActivityEvent,
) {
  const action = getActivityAction(event);
  if (stepId === "create_or_customize_survey") {
    return (
      event.name === "survey.qr_template.applied" ||
      (event.name === "app.action" &&
        (action === "api.mutation" ||
          action.includes("survey_settings") ||
          action.includes("block_create") ||
          action.includes("question") ||
          action.includes("survey")))
    );
  }
  if (stepId === "send_or_share_survey") {
    return (
      event.name === "survey.qr_template.applied" ||
      (event.name === "app.action" &&
        (action.includes("qr") ||
          action.includes("share") ||
          action.includes("send") ||
          action.includes("email")))
    );
  }
  if (stepId === "support") {
    return event.name === "support.chat_opened";
  }
  return false;
}

export function GuidedOnboardingPanel() {
  const { user } = useAuth();
  const pathname = usePathname() || "/";
  const router = useRouter();
  const queryClient = useQueryClient();
  const isEligibleUser = Boolean(user && !user.isSuperAdmin);
  const [collapsed, setCollapsed] = useState(false);
  const autoCompletedStepsRef = useRef<Set<string>>(new Set());

  const onboardingQuery = useQuery({
    queryKey: ONBOARDING_QUERY_KEY,
    queryFn: () => apiRequest<OnboardingStateResponse>("/api/users/me/onboarding"),
    enabled: isEligibleUser,
    staleTime: 30_000,
  });

  const progressMutation = useMutation({
    mutationFn: (payload: {
      action: OnboardingProgressAction;
      step?: OnboardingStepId | null;
      route?: string;
    }) =>
      apiRequest<OnboardingStateResponse>("/api/users/me/onboarding/progress", {
        method: "POST",
        body: JSON.stringify(payload),
      }),
    onSuccess: (data, variables) => {
      queryClient.setQueryData(ONBOARDING_QUERY_KEY, data);
      queryClient.invalidateQueries({ queryKey: ["auth", "session"] });
      if (variables.action === "show") {
        trackActivity("onboarding.shown", {
          route: variables.route,
          step: variables.step,
          flowRole: data.flowRole,
        }, "Onboarding shown");
      }
    },
  });

  const state = onboardingQuery.data;
  const onboarding = state?.onboarding || user?.preferences?.onboarding;
  const steps = useMemo(() => state?.steps || [], [state?.steps]);
  const shouldShow = Boolean(state?.shouldShow);
  const currentStep = useMemo(() => {
    if (!onboarding || steps.length === 0) return null;
    return steps.find((step) => step.id === onboarding.currentStep) || steps[0] || null;
  }, [onboarding, steps]);
  const completedCount = onboarding?.completedSteps?.length || 0;
  const totalCount = steps.length;
  const isCurrentRoute = currentStep
    ? pathname === currentStep.route || pathname.startsWith(`${currentStep.route}/`)
    : false;
  const isSupportStep = currentStep?.id === "support";
  const completeStep = useCallback(
    (step: OnboardingStepId, source: "route" | "activity" | "manual") => {
      if (!currentStep || currentStep.id !== step || progressMutation.isPending) return;
      if (onboarding?.completedSteps?.includes(step)) return;
      if (source !== "manual") {
        const key = `${source}:${step}`;
        if (autoCompletedStepsRef.current.has(key)) return;
        autoCompletedStepsRef.current.add(key);
      }
      progressMutation.mutate({
        action: "complete_step",
        step,
        route: pathname,
      });
    },
    [currentStep, onboarding?.completedSteps, pathname, progressMutation],
  );
  const openSupportChat = useCallback(() => {
    const supportLauncher = document.querySelector<HTMLButtonElement>(
      `button[aria-label="Open support chat"], button[aria-label="Open Ask ${AGENT_DISPLAY_NAME}"]`,
    );
    if (supportLauncher) {
      supportLauncher.click();
      return;
    }

    const openSupportChatDialog = document.querySelector(
      `button[aria-label="Close support chat"], button[aria-label="Close ${AGENT_DISPLAY_NAME}"]`,
    );
    if (openSupportChatDialog) {
      completeStep("support", "manual");
    }
  }, [completeStep]);

  useEffect(() => {
    if (!shouldShow || !currentStep || onboarding?.lastShownAt) return;
    progressMutation.mutate({
      action: "show",
      step: currentStep.id,
      route: pathname,
    });
    // We only want to mark the first visible render for this onboarding run.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldShow, currentStep?.id, onboarding?.lastShownAt]);

  useEffect(() => {
    if (!shouldShow || !currentStep) return;
    if (ROUTE_COMPLETED_STEPS.has(currentStep.id) && isRouteMatch(pathname, currentStep.route)) {
      completeStep(currentStep.id, "route");
      return;
    }
    if (currentStep.id === "create_or_customize_survey" && isSurveyDetailRoute(pathname)) {
      completeStep(currentStep.id, "route");
    }
  }, [completeStep, currentStep, pathname, shouldShow]);

  useEffect(() => {
    if (!shouldShow || !currentStep) return;
    return subscribeToActivityEvents((event) => {
      if (shouldCompleteStepFromActivity(currentStep.id, event)) {
        completeStep(currentStep.id, "activity");
      }
    });
  }, [completeStep, currentStep, shouldShow]);

  if (!isEligibleUser || !shouldShow || !currentStep || pathname.startsWith("/admin")) {
    return null;
  }

  const completeCurrentStep = () => {
    completeStep(currentStep.id, "manual");
  };

  const endWalkthrough = () => {
    progressMutation.mutate({
      action: "dismiss",
      step: currentStep.id,
      route: pathname,
    });
  };

  const completeWalkthrough = () => {
    progressMutation.mutate({
      action: "complete",
      step: currentStep.id,
      route: pathname,
    });
  };

  return (
    <aside
      className={styles.panel}
      data-collapsed={collapsed ? "true" : undefined}
      aria-label="Guided walkthrough"
    >
      <div className={styles.header}>
        <div>
          <div className={styles.eyebrow}>
            {state?.isDemoAccount
              ? `${state.demoAccountType || "Preview workspace"} walkthrough`
              : state?.flowRole === "ORG_ADMIN" ? "Admin walkthrough" : "Member walkthrough"}
          </div>
          <h2 className={styles.title}>{currentStep.title}</h2>
        </div>
        <div className={styles.headerActions}>
          <span className={styles.count}>
            {Math.min(completedCount + 1, totalCount)}/{totalCount}
          </span>
          <button
            type="button"
            className={styles.collapseButton}
            onClick={() => setCollapsed((value) => !value)}
            aria-label={collapsed ? "Expand walkthrough" : "Collapse walkthrough"}
            aria-expanded={!collapsed}
          >
            <Icon name={collapsed ? "ChevronUp" : "ChevronDown"} size={16} />
          </button>
        </div>
      </div>

      {!collapsed && (
        <>
          <p className={styles.description}>
            {state?.isDemoAccount && currentStep.id === "dashboard_overview"
              ? `This preview workspace is preloaded with sample ${state.demoAccountType || "industry"} surveys, responses, and email metrics. ${currentStep.description}`
              : currentStep.description}
          </p>

          <div className={styles.progressTrack} aria-hidden="true">
            <div
              className={styles.progressFill}
              style={{ width: `${totalCount ? Math.min(100, ((completedCount + 1) / totalCount) * 100) : 0}%` }}
            />
          </div>

          <div className={styles.actions}>
            {isSupportStep && isCurrentRoute ? (
              <Button
                variant="primary"
                size="sm"
                onClick={openSupportChat}
                loading={progressMutation.isPending}
              >
                Open support chat
              </Button>
            ) : isCurrentRoute ? (
              <Button
                variant="primary"
                size="sm"
                onClick={currentStep.id === "next_step" ? completeWalkthrough : completeCurrentStep}
                loading={progressMutation.isPending}
              >
                {currentStep.id === "next_step" ? "Finish walkthrough" : "Mark complete"}
              </Button>
            ) : (
              <Button
                variant="primary"
                size="sm"
                onClick={() => router.push(currentStep.route)}
              >
                {currentStep.actionLabel}
              </Button>
            )}
            <Button
              variant="secondary"
              size="sm"
              onClick={endWalkthrough}
              loading={progressMutation.isPending}
            >
              End walkthrough
            </Button>
          </div>

          <div className={styles.footer}>
            <Icon name="Info" size={14} />
            <span>You can restart this later by asking your account manager.</span>
          </div>
        </>
      )}
    </aside>
  );
}
